/**
 * Общий протокол между виджетом (браузер) и сервером DreamTeam Chat.
 * Только типы и константы — без зависимостей, чтобы пакет можно было
 * импортировать и в браузер, и в Node.
 */

export const PROTOCOL_VERSION = 1;

/* ------------------------------------------------------------------ */
/* Заявки (тикеты)                                                     */
/* ------------------------------------------------------------------ */

/**
 * Жизненный цикл заявки.
 * collecting  — пользователь ещё дописывает: сообщения склеиваются в эту заявку;
 * submitted   — заявка передана конвейеру агентов (по таймеру тишины или кнопкой);
 * in_progress — агенты работают;
 * question    — агенту нужен ответ пользователя;
 * escalated   — затрагивает БД/авторизацию/оплату, ждёт решения человека;
 * feature_ready — функция сделана и включена автору (за флагом), ждёт его ответа «подходит / нужно поправить»;
 * done        — закрыта успешно;
 * rejected    — отклонена (мусор, не по теме, дубль);
 * cancelled   — отменена пользователем.
 */
export const TICKET_STATUSES = [
  'collecting',
  'submitted',
  'in_progress',
  'question',
  'escalated',
  'feature_ready',
  'done',
  'rejected',
  'cancelled',
] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const CLOSED_TICKET_STATUSES: readonly TicketStatus[] = ['done', 'rejected', 'cancelled'];

export interface Ticket {
  id: string;
  appId: string;
  userId: string;
  /** Номер заявки внутри приложения (для людей). */
  number: number;
  title: string;
  status: TicketStatus;
  /** Ключ функции за флагом в реестре приложения, которой закрыта заявка (ставит конвейер). */
  featureKey: string | null;
  /** Номер issue в GitHub-репозитории приложения (если синхронизирована). */
  githubIssueNumber: number | null;
  githubIssueUrl: string | null;
  createdAt: string;
  updatedAt: string;
  lastUserMessageAt: string | null;
  submittedAt: string | null;
}

/* ------------------------------------------------------------------ */
/* Сообщения                                                           */
/* ------------------------------------------------------------------ */

export type MessageAuthor = 'user' | 'system' | 'agent';

/**
 * user:   text | voice | screenshot | file (может быть комбинация — kind берётся по «главному» вложению)
 * system: status | feature
 * agent:  question | text
 */
export type MessageKind = 'text' | 'voice' | 'screenshot' | 'file' | 'status' | 'question' | 'feature';

export type TranscriptStatus = 'none' | 'pending' | 'done' | 'failed';

export interface Attachment {
  id: string;
  kind: 'screenshot' | 'audio' | 'file';
  mime: string;
  size: number;
  /** Имя файла, как его приложил пользователь (kind = file). */
  name?: string | null;
  /** Подписанный URL (короткоживущий) для <img>/<audio>. */
  url: string;
  width?: number | null;
  height?: number | null;
  durationMs?: number | null;
  /** Для скриншота — выделенная область и DOM-элементы внутри. */
  selection?: ScreenshotSelection | null;
}

export interface Message {
  id: string;
  ticketId: string;
  appId: string;
  userId: string;
  author: MessageAuthor;
  kind: MessageKind;
  text: string;
  transcript: string | null;
  transcriptStatus: TranscriptStatus;
  attachments: Attachment[];
  /** Для question — варианты быстрых ответов; для feature — функция, которой закрыта заявка; для status — новый статус. */
  meta: MessageMeta;
  replyTo: string | null;
  /** ID комментария в GitHub issue, к которому привязано сообщение. */
  githubCommentId: number | null;
  githubCommentUrl: string | null;
  /** Количество событий браузерного контекста, приложенных к сообщению. */
  contextEventCount: number;
  createdAt: string;
}

export interface MessageMeta {
  status?: TicketStatus;
  options?: string[];
  feature?: FeatureRef;
  [k: string]: unknown;
}

/** Функция за флагом, которой закрыта заявка. Реестр функций живёт в приложении; здесь — ссылка на него. */
export interface FeatureRef {
  key: string;
  title: string;
  description?: string;
  /** Страница и метка элемента, где функцию видно. */
  route?: string | null;
  anchor?: string | null;
}

/* ------------------------------------------------------------------ */
/* Контекст браузера                                                   */
/* ------------------------------------------------------------------ */

export interface ElementDescriptor {
  /** Уникальный CSS-селектор (по возможности через id / data-testid). */
  selector: string;
  tag: string;
  id?: string;
  classes?: string[];
  role?: string;
  ariaLabel?: string;
  testId?: string;
  name?: string;
  type?: string;
  href?: string;
  placeholder?: string;
  /** Видимый текст, обрезанный. */
  text?: string;
  /** Открывающий тег, обрезанный. */
  html?: string;
  rect: Rect;
  /** Как элемент соотносится с выделением. */
  relation?: 'target' | 'inside' | 'container' | 'intersects';
  depth?: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ScreenshotSelection {
  /** Прямоугольник выделения в координатах вьюпорта (CSS px). */
  rect: Rect;
  /** Точка, где пользователь начал выделение (клик). */
  point: { x: number; y: number };
  scroll: { x: number; y: number };
  viewport: { width: number; height: number; dpr: number };
  url: string;
  /** Элемент под точкой клика (самый глубокий). */
  target: ElementDescriptor | null;
  /** Элементы, целиком попавшие в прямоугольник, плюс ближайший общий контейнер. */
  elements: ElementDescriptor[];
  truncated?: boolean;
}

export type CaptureEvent =
  | { t: number; type: 'nav'; url: string; title?: string; from?: string; via: 'load' | 'push' | 'replace' | 'pop' | 'hash' }
  | {
      t: number;
      type: 'click';
      url: string;
      x: number;
      y: number;
      target: Omit<ElementDescriptor, 'rect'> & { rect?: Rect };
    }
  | {
      t: number;
      type: 'net';
      transport: 'fetch' | 'xhr';
      method: string;
      url: string;
      status: number;
      ok: boolean;
      durationMs: number;
      requestHeaders?: Record<string, string>;
      requestBody?: string;
      responseHeaders?: Record<string, string>;
      responseBody?: string;
      error?: string;
      truncated?: boolean;
    }
  | { t: number; type: 'console'; level: 'error' | 'warn'; message: string }
  | { t: number; type: 'error'; message: string; source?: string; stack?: string };

export interface EnvironmentSnapshot {
  url: string;
  title: string;
  referrer: string;
  userAgent: string;
  language: string;
  timezone: string;
  viewport: { width: number; height: number; dpr: number };
  screen: { width: number; height: number };
  /** Произвольное состояние приложения от хоста (сборка, роль, включённые функции…). */
  appState?: Record<string, unknown>;
}

export interface MessageContext {
  env: EnvironmentSnapshot;
  events: CaptureEvent[];
  /** Сколько событий было отброшено ограничением буфера. */
  dropped: number;
  consent: boolean;
  /** Запись действий велась по кнопке: когда началась и закончилась. */
  recording?: { startedAt: number; stoppedAt: number } | null;
}

/* ------------------------------------------------------------------ */
/* Widget API                                                          */
/* ------------------------------------------------------------------ */

export interface BootstrapResponse {
  protocol: number;
  app: { id: string; name: string; groupingWindowMinutes: number; idleSubmitSeconds: number; groupingMode: 'window' | 'single' };
  user: { id: string; name: string | null };
  /** Заявка, к которой прикрепится следующее сообщение (если есть). */
  currentTicket: Ticket | null;
  /** Заявки пользователя, новые сверху (вкладка «Мои заявки»). */
  tickets: Ticket[];
}

export interface SendMessagePayload {
  /** Идемпотентность: повторная отправка с тем же clientId не создаёт дубль. */
  clientId: string;
  text?: string;
  /** Явно дописать в эту заявку (например, ответ на вопрос). */
  ticketId?: string | null;
  /** Принудительно начать новую заявку. */
  newTicket?: boolean;
  replyTo?: string | null;
  context?: MessageContext | null;
  /** Метаданные файлов в том же порядке, что и части multipart. */
  screenshots?: Array<{ field: string; selection: ScreenshotSelection; width: number; height: number }>;
  audio?: { field: string; durationMs: number } | null;
  /** Приложенные файлы (таблицы, документы, картинки) — части multipart file0..N. */
  files?: Array<{ field: string; name: string; size: number; mime: string }>;
}

export interface SendMessageResponse {
  message: Message;
  ticket: Ticket;
  /** true, если сообщение открыло новую заявку. */
  ticketCreated: boolean;
}

export interface HistoryResponse {
  tickets: Ticket[];
  messages: Message[];
  hasMore: boolean;
}

/** События, приходящие в виджет по SSE. */
export type StreamEvent =
  | { type: 'message.created'; message: Message }
  | { type: 'message.updated'; message: Message }
  | { type: 'ticket.updated'; ticket: Ticket }
  | { type: 'ping'; t: number };

/* ------------------------------------------------------------------ */
/* Pipeline (исходящие вебхуки к конвейеру агентов)                    */
/* ------------------------------------------------------------------ */

export type PipelineEvent =
  | { type: 'ticket.created'; appId: string; ticket: Ticket }
  | { type: 'ticket.message'; appId: string; ticket: Ticket; message: Message; context: MessageContext | null }
  | { type: 'ticket.submitted'; appId: string; ticket: Ticket }
  | { type: 'ticket.cancelled'; appId: string; ticket: Ticket }
  | { type: 'ticket.verdict'; appId: string; ticket: Ticket; verdict: 'ok' | 'fix' };

export const STATUS_LABELS_RU: Record<TicketStatus, string> = {
  collecting: 'Собираем детали',
  submitted: 'Передана в работу',
  in_progress: 'В разработке',
  question: 'Нужен ваш ответ',
  escalated: 'На согласовании',
  feature_ready: 'Функция готова',
  done: 'Готово',
  rejected: 'Отклонена',
  cancelled: 'Отменена',
};

export const STATUS_LABELS_EN: Record<TicketStatus, string> = {
  collecting: 'Collecting details',
  submitted: 'Submitted',
  in_progress: 'In development',
  question: 'Needs your answer',
  escalated: 'Under review',
  feature_ready: 'Feature ready',
  done: 'Done',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};
